import { Button, Box, Alert } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useState } from 'react';
import { loginRequest } from '../config/msalConfig';

interface MicrosoftSignInButtonProps {
  variant: 'login' | 'signup';
  fullWidth?: boolean;
  companyName?: string;
  agreeToTerms?: boolean;
  marketingConsent?: boolean;
  /** Called before the redirect; return false to cancel (e.g. failed validation) */
  onBeforeRedirect?: () => Promise<boolean> | boolean;
}

export function MicrosoftSignInButton({
  variant,
  fullWidth = true,
  companyName,
  agreeToTerms,
  marketingConsent,
  onBeforeRedirect
}: MicrosoftSignInButtonProps) {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Run pre-redirect validation (e.g. require company name + terms on signup page)
      if (onBeforeRedirect) {
        const canProceed = await onBeforeRedirect();
        if (!canProceed) {
          setIsLoading(false);
          return;
        }
      }

      // Store signup data so MicrosoftCallbackPage can retrieve it after the redirect
      if (companyName) {
        sessionStorage.setItem('microsoft_signup_company', companyName);
      }
      if (agreeToTerms !== undefined) {
        sessionStorage.setItem('microsoft_signup_agreeToTerms', String(agreeToTerms));
      }
      if (marketingConsent !== undefined) {
        sessionStorage.setItem('microsoft_signup_marketingConsent', String(marketingConsent));
      }

      // loginRedirect navigates the entire page to Microsoft — avoids COOP popup issues
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'select_account'
      });
      // Page navigates away — code below won't run on success
    } catch (err: any) {
      console.error('Microsoft login error:', err);
      setError(err.message || 'Microsoft login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        fullWidth={fullWidth}
        variant="outlined"
        size="large"
        onClick={handleMicrosoftLogin}
        disabled={isLoading}
        sx={{
          borderColor: '#5E5E5E',
          color: '#5E5E5E',
          textTransform: 'none',
          fontWeight: 600,
          py: 1.5,
          '&:hover': {
            borderColor: '#2F2F2F',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&:disabled': {
            borderColor: '#E0E0E0',
            color: '#9E9E9E',
          }
        }}
      >
        <Box
          component="img"
          src="/microsoft-logo.svg"
          alt="Microsoft"
          sx={{ mr: 1.5, width: 20, height: 20 }}
        />
        {isLoading
          ? 'Signing in...'
          : variant === 'signup'
            ? 'Sign up with Microsoft'
            : 'Sign in with Microsoft'
        }
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
}
